import React, {
  type ChangeEvent,
  type KeyboardEvent,
  memo,
  type ReactElement,
  useState,
} from 'react';
import { Box, Icon, InlineMessage, InputField } from '@saviynt/design-system';
import cx from 'classnames';

import { testIds } from '../../../tests';
import { CF_NEU_STL, SEARCH_TEXT_LENGTH } from '../../constants';
import { useDebounceForFunctions } from '../../hooks';

import './SodSearch.css';

interface SodSearchProps {
  searchPlaceholder: string;
  inlineMesssage: string;
  searchDebounce: number;
  isDisabled: boolean | undefined;
  handleSearch: (state: string) => void | false | undefined;
  className?: string;
}

/**
 * SodSearch is a controlled search input component with:
 * - debounced search callback,
 * - expandable/collapsible UI,
 * - optional input validation,
 * - inline error messaging,
 * and styling from the Saviynt design system.
 *
 * It triggers `handleSearch` with trimmed input after a specified debounce delay.
 *
 * @component
 * @param {Object} props - Props for SodSearch component
 * @param {string} props.searchPlaceholder - Placeholder text shown in the input field
 * @param {string} props.inlineMesssage - Message shown when the input is invalid (e.g., too short)
 * @param {number} props.searchDebounce - Debounce duration in milliseconds before invoking `handleSearch`
 * @param {boolean | undefined} props.isDisabled - Disables the input field if `true`
 * @param {(state: string) => void | false | undefined} props.handleSearch - Function to be called with the search text
 * @param {string} [props.className] - Optional class name to apply to the outer container
 *
 * @returns {ReactElement} The rendered search input component
 */
function SodSearch({
  searchPlaceholder = '',
  inlineMesssage = '',
  searchDebounce,
  isDisabled = false,
  handleSearch,
  className,
}: SodSearchProps): ReactElement {
  const [searchText, setSearchText] = useState('');
  const [isSearchExpaded, setIsSearchExpaded] = useState(false);
  const [isSearchAlert, setIsSearchAlert] = useState(false);

  const searchBoxClasses = cx(
    'SodControls-searchField',
    isSearchExpaded
      ? 'SodControls-searchField--isExpanded'
      : 'SodControls-searchField--isCollapsed',
    className
  );

  const handleLocalSearch = (text: string) => {
    if (text.trim().length < SEARCH_TEXT_LENGTH && text.length !== 0) {
      setIsSearchAlert(true);

      return;
    }

    setIsSearchAlert(false);
    handleSearch(text.trim());
  };

  const handleDebouncedSearch = useDebounceForFunctions(
    (e: ChangeEvent<HTMLInputElement>) => handleLocalSearch(e.target.value),
    searchDebounce
  );

  const handleSearchBoxClose = () => {
    handleSearch('');
    setIsSearchExpaded(false);
    setIsSearchAlert(false);
  };

  return (
    <Box
      tag='div'
      className={searchBoxClasses}
      tabIndex={0}
      onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter') {
          setIsSearchExpaded(true);
        }
      }}>
      <InputField
        name='violation-name-search'
        placeholder={searchPlaceholder}
        kind='searchCollapse'
        size='medium'
        prefixIcon={<Icon kind='search' color={CF_NEU_STL} />}
        value={searchText}
        shouldDisallowBrackets
        setValue={setSearchText}
        onChange={handleDebouncedSearch}
        resetValue={handleSearchBoxClose}
        isSearchExpanded={isSearchExpaded}
        setIsSearchExpanded={setIsSearchExpaded}
        dataTestId={testIds.SOD_CONTROLS_SEARCH_BOX}
        isDisabled={isDisabled}
      />
      {isSearchAlert && isSearchExpaded && (
        <InlineMessage
          text={inlineMesssage}
          colorTheme='critical'
          size='small'
          leftIcon={<Icon kind='AlertCriticalFilled' color='critical-700' />}
        />
      )}
    </Box>
  );
}

SodSearch.defaultProps = {
  className: null,
};

export default memo(SodSearch);
